import { Slots } from '@jooxter/api';
import { DataPeriodEnum, fromISO, getStandardTime, IQuickTimeSlot } from '@jooxter/utils';
import { Duration } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useWorkHours } from '../services/useWorkHours';
import { useFetchUser } from '../../queries';

export const useDefaultDurationSlotGenerator = (
  date: string,
  slots?: Slots,
  timezone?: string
): { durationSlots: IQuickTimeSlot[]; isLoading: boolean } => {
  const { user, isLoading: isUserLoading } = useFetchUser();
  const { getStart, isLoading: isWorkHoursLoading } = useWorkHours(user?.id);
  const dtDate = useMemo(() => fromISO(date, timezone), [date, timezone]);

  const getDurationSlot = useCallback(
    (duration: Duration): IQuickTimeSlot => {
      const start = getStart(dtDate, getStandardTime(dtDate));
      const hourMin = start.split(':');
      const end = dtDate
        .set({
          hour: +hourMin[0],
          minute: +hourMin[1],
        })
        .plus(duration);

      let formattedDuration = duration.shiftTo('hours', 'minutes');

      if (formattedDuration.minutes % 60 === 0) {
        formattedDuration = formattedDuration.shiftTo('hours');
      }

      if (formattedDuration.hours === 0) {
        formattedDuration = formattedDuration.shiftTo('minutes');
      }

      return {
        start: dtDate,
        end,
        generatedByDefault: true,
        period: DataPeriodEnum.Day,
        text: formattedDuration.toHuman({ unitDisplay: 'short' }) ?? '',
      };
    },
    [dtDate, getStart]
  );

  const durationSlots = useMemo(() => {
    return (
      slots?.durations
        ?.map((duration) => {
          if (!duration) {
            return null;
          }

          return getDurationSlot(Duration.fromObject({ minutes: duration }));
        })
        .filter((d): d is IQuickTimeSlot => !!d) ?? []
    );
  }, [slots, getDurationSlot]);

  return { durationSlots, isLoading: isUserLoading || isWorkHoursLoading };
};
